import { useEffect, useState } from 'react';
import axios from 'axios';
import { ChangeSpec } from './types';

const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4000';

/**
 * The change spec, one-homed (2026-09-01, Fredrik). It rendered only inside
 * DecisionView's Final Decision block, so the producer standing in a
 * revision_needed / pending-fixes cycle — the person the spec is FOR — had
 * no way to see it. Same second-path-lacks-what-the-first-has shape as the
 * revision controls; extracted rather than copied so the two mounts cannot
 * drift on the thumbnails or the rich-clipboard copy.
 */
interface ChangeSpecPanelProps {
  cycleId: string;
  getAuthHeaders: () => Promise<Record<string, string>>;
  /** Known apply-count for the closed-state button label (DM has it from the
      comments; producers omit it and the label stays plain). */
  toApplyCount?: number;
  /** Open with the spec already loaded — for surfaces where the spec is the
      point (the producer's revision panel), not an optional detail. */
  autoLoad?: boolean;
}

const ChangeSpecPanel: React.FC<ChangeSpecPanelProps> = ({ cycleId, getAuthHeaders, toApplyCount, autoLoad = false }) => {
  const [spec, setSpec] = useState<ChangeSpec | null>(null);
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const load = async () => {
    try {
      setError(null);
      const headers = await getAuthHeaders();
      const r = await axios.get(`${backendUrl}/api/content-review/cycles/${cycleId}/change-spec`, { headers });
      setSpec(r.data);
      setOpen(true);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to build change spec');
    }
  };

  useEffect(() => {
    if (autoLoad) void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoLoad, cycleId]);

  const copy = async () => {
    if (!spec?.markdown) return;
    try {
      // Both flavors: Word/Docs take the HTML (thumbnails ride along as
      // embedded images); plain-text targets get the markdown.
      if (spec.html && typeof ClipboardItem !== 'undefined' && navigator.clipboard.write) {
        await navigator.clipboard.write([new ClipboardItem({
          'text/html': new Blob([spec.html], { type: 'text/html' }),
          'text/plain': new Blob([spec.markdown], { type: 'text/plain' })
        })]);
      } else {
        await navigator.clipboard.writeText(spec.markdown);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      setError('Clipboard unavailable — select the text and copy manually.');
    }
  };

  return (
    <div style={{ marginBottom: 14 }} data-testid="change-spec-panel">
      <button className="review-btn review-btn-outline" style={{ fontSize: 12, padding: '4px 10px' }} onClick={open ? () => setOpen(false) : load}>
        {open ? 'Hide change spec' : `Change spec${typeof toApplyCount === 'number' ? ` (${toApplyCount} to apply)` : spec ? ` (${spec.itemCount} to apply)` : ''}`}
      </button>
      {error && <span style={{ marginLeft: 8, fontSize: 12, color: '#dc3545' }}>{error}</span>}
      {open && spec && (
        <div style={{ marginTop: 8, border: '1px solid #d0d7de', borderRadius: 8, padding: 10, background: '#fafbfc' }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 6, fontSize: 12 }}>
            <span style={{ fontWeight: 600, color: '#123250' }}>{spec.label}</span>
            {spec.fallback && (
              <span style={{ color: '#856404', background: '#fff3cd', padding: '1px 8px', borderRadius: 4 }} title={spec.reason}>
                fallback — no amendment channel for this content type
              </span>
            )}
            {!spec.executed && (
              <span style={{ color: '#6c757d' }}>CellcoHub cannot write into this format — apply by hand in the working file</span>
            )}
            <button className="review-btn review-btn-outline" style={{ fontSize: 12, padding: '2px 8px', marginLeft: 'auto' }} onClick={copy}>
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
          {/* The markdown can't carry images — marked-area comments show
              their crop here so "slide 4 · marked area" in the text below
              has a face (Fredrik, 2026-09-01). */}
          {spec.apply.some(it => it.anchorThumbnail) && (
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
              {spec.apply.filter(it => it.anchorThumbnail).map(it => (
                <figure key={it.commentId} style={{ margin: 0, maxWidth: 220 }}>
                  <img src={it.anchorThumbnail} alt={it.anchor.label} style={{ maxWidth: '100%', maxHeight: 110, display: 'block', border: '1px solid #ddd', borderRadius: 4 }} />
                  <figcaption style={{ fontSize: 11, color: '#555', marginTop: 2 }}>
                    {it.anchor.label} — {it.issue.length > 60 ? it.issue.slice(0, 60) + '…' : it.issue}
                  </figcaption>
                </figure>
              ))}
            </div>
          )}
          <textarea readOnly value={spec.markdown} style={{ width: '100%', minHeight: 160, fontFamily: 'monospace', fontSize: 12, border: '1px solid #e1e4e8', borderRadius: 6, padding: 8, background: '#fff' }} />
        </div>
      )}
    </div>
  );
};

export default ChangeSpecPanel;
